<template>
  <div
    :style="colorOverrides"
    class="event-list-item"
    data-cy="event-list-item"
    :class="{
      active: displayAddButton,
    }"
    @mouseenter="showAddButton"
    @mouseleave="resetState"
    @focusin="showAddButton"
    @focusout="handleBlur"
  >
    <div class="left">
      <div
        class="date"
        :class="{ 'multi-day': isMultiDayEvent }"
      >
        <EventListDate
          :month="eventData.month"
          :start-date="eventData.start_at"
          :end-date="isMultiDayEvent ? null : eventData.end_at"
          :is-all-day="eventData.all_day"
        />
        <template v-if="isMultiDayEvent">
          <EventLeftArrowIcon class="date-arrow" />
          <EventListDate
            :is-multi="isMultiDayEvent"
            :month="eventData.month"
            :start-date="isMultiDayEvent ? null : eventData.start_at"
            :end-date="eventData.end_at"
            :is-all-day="eventData.all_day"
          />
        </template>
      </div>
      <div class="content">
        <button
          class="title"
          @click="viewEvent"
        >
          {{ eventData.title }}
        </button>
        <div
          v-linkify
          class="description"
          v-html="sanitizedEventDescription"
        />
        <div class="venue">
          {{ eventData.venue }}
        </div>
        <div class="tags">
          <EventListTag
            v-for="tag in eventTags"
            :key="tag"
            :display-text="tag"
            variant="pill"
          />
        </div>
        <div
          v-if="isMobile"
          class="right"
        >
          <div class="button">
            <button
              v-show="!displayCalendarButtons"
              ref="itemButton"
              type="button"
              class="button-container add-to-calendar-trigger"
              :aria-expanded="String(displayCalendarButtons)"
              :aria-controls="calendarOptionsId"
              @click="showCalendarButtons"
            >
              <AddToCalendarIcon />
              <span class="button-text">
                {{ $t('events.addToCalendar') }}
              </span>
            </button>
            <EventListButtons
              :id="calendarOptionsId"
              ref="calendarOptions"
              :show-calendar-buttons="displayCalendarButtons"
              :handle-back-button-click="handleBackButtonClick"
              :color-overrides="colorOverrides"
              :event-data="eventDataWithType"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="!isMobile"
      class="right right-reveal"
    >
      <div class="button">
        <button
          v-show="!displayCalendarButtons"
          ref="itemButton"
          type="button"
          class="button-container add-to-calendar-trigger"
          :aria-expanded="String(displayCalendarButtons)"
          :aria-controls="calendarOptionsId"
          @click="showCalendarButtons"
        >
          <AddToCalendarIcon />
          <span class="button-text">
            {{ $t('events.addToCalendar') }}
          </span>
        </button>
        <EventListButtons
          :id="calendarOptionsId"
          ref="calendarOptions"
          :show-calendar-buttons="displayCalendarButtons"
          :handle-back-button-click="handleBackButtonClick"
          :color-overrides="colorOverrides"
          :event-data="eventDataWithType"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onBeforeMount, useTemplateRef } from 'vue';
import DOMPurify from 'dompurify';
import { useScreenWidth } from '../../../../../composables/useScreenWidth.js';
import EventListDate from './EventListDate.vue';
import EventLeftArrowIcon from '../../icons/EventLeftArrowIcon.vue';
import EventListTag from './EventListTag.vue';
import AddToCalendarIcon from '../../icons/AddToCalendarIcon.vue';
import EventListButtons from './EventListButtons.vue';
import tinycolor from 'tinycolor2';
import { useEventsStore } from '../../../../../stores/eventsStore.js';
import { useMainStore } from '../../../../../stores/mainStore.js';

defineOptions({
  name: 'EventListItem',
});

const props = defineProps({
  eventData: {
    type: Object,
    default: null,
  },
});

const eventsStore = useEventsStore();
const mainStore = useMainStore();

const sections = computed(() => eventsStore.sections);
const headingColor = computed(() => mainStore.getThemeHeadingColor);

const displayAddButton = ref(false);
const displayCalendarButtons = ref(false);
const sanitizedEventDescription = ref('');
let isOpeningCalendarButtons = false;

const { screenSize } = useScreenWidth();

const itemButtonRef = useTemplateRef('itemButton');
const calendarOptionsRef = useTemplateRef('calendarOptions');

const calendarOptionsId = computed(() => `calendar-options-${props.eventData?.id}`);

const colorOverrides = computed(() => {
  const color = headingColor.value;
  if (!color) {
    return {};
  }

  const transparentBgColor = tinycolor(`${color}1A`);
  const alpha = transparentBgColor.getAlpha();
  const mixedOpaqueColor = tinycolor.mix('#FFFFFF', transparentBgColor, alpha * 100);
  const finalOpaqueHex = mixedOpaqueColor.toHexString();

  return {
    '--button-outline': color,
    // Primary
    '--button-primary-bg': `${color}1A`,
    '--button-primary-bg-hover': color,
    '--button-primary-text': tinycolor.mostReadable(finalOpaqueHex, [color, '#fff', '#000']),
    '--primary-button-text': tinycolor.mostReadable(color, ['#fff', '#000']),
    '--event-item-hover': `${color}1A`,
  };
});

const isMultiDayEvent = computed(() => {
  if (!props.eventData?.start_at || !props.eventData?.end_at) return false;
  const startDate = props.eventData.start_at.split('T')[0];
  const endDate = props.eventData.end_at.split('T')[0];
  return startDate !== endDate;
});

const eventTags = computed(() => {
  const tags = [];
  if (!props.eventData) return tags;
  if (sections.value.length > 1) {
    tags.push(props.eventData.custom_section_name);
  }
  tags.push(...props.eventData.filter_name);
  return tags;
});

const isMobile = computed(() => screenSize.value < 960);

const eventDataWithType = computed(() => ({
  ...props.eventData,
  type: 'event',
}));

onBeforeMount(() => {
  sanitizedEventDescription.value = DOMPurify.sanitize(props.eventData?.description ?? '');
});

function viewEvent() {
  return navigateTo({
    query: {
      id: props.eventData.id,
    },
  });
}

function showAddButton() {
  if (isMobile.value) return;
  displayAddButton.value = true;
}

async function showCalendarButtons() {
  // Hiding the trigger blurs it before focus reaches the first calendar button; ignore that blur.
  isOpeningCalendarButtons = true;
  displayCalendarButtons.value = true;
  try {
    await nextTick();
    calendarOptionsRef.value?.$el?.querySelector('button')?.focus();
  } finally {
    isOpeningCalendarButtons = false;
  }
}

function resetState() {
  displayAddButton.value = false;
  displayCalendarButtons.value = false;
}

async function handleBackButtonClick() {
  displayCalendarButtons.value = false;
  await nextTick();
  itemButtonRef.value?.focus?.();
}

function handleBlur(event) {
  if (isOpeningCalendarButtons) return;
  if (!event.currentTarget.contains(event.relatedTarget)) {
    resetState();
  }
}
</script>

<style lang="scss" scoped>
@use '../../../../global/scss/mixins/flex_mixin';
@use '../../../../global/scss/mixins/breakpoints';

$buttonMinHeight: 160px;
$buttonMinWidth: 136px;
$buttonMargin: 8px;
$sidesWidth: 192px;

.event-list-item {
  @include flex_mixin.flex-column;
  border-bottom: 1px solid rgba(0, 73, 144, 0.1);
  padding: 0;
  justify-content: space-between;
  min-height: 192px;
  @media (min-width: breakpoints.$md) {
    @include flex_mixin.flex-row;
  }

  &.active {
    background: rgba(0, 73, 144, 0.06);
  }

  &:hover {
    background: var(--event-item-hover);
  }

  &:first-of-type {
    margin-top: 32px;
  }

  .left {
    @include flex_mixin.flex-row;
    flex: 1;
    padding: 24px 0;

    .date {
      @include flex_mixin.flex-row;
      justify-content: left;
      align-items: flex-start;
      padding: 0 0 0 34px;
    }

    .content {
      @include flex_mixin.flex-column;
      flex: 1;
    }
  }

  .right {
    display: none;
    @include flex_mixin.flex-row;
    width: 151px;

    @media (min-width: breakpoints.$md) {
      @include flex_mixin.flex-column;
      min-width: $buttonMinWidth + ($buttonMargin * 2);
    }
  }

  .title {
    font-style: normal;
    font-weight: 500;
    font-size: 20px;
    line-height: 20px;
    margin-bottom: 16px;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    text-align: left;
  }
  .description {
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 8px;
    word-break: break-word;
    :deep(a) {
      color: var(--links-color);
    }
  }
  .venue {
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 18px;
    color: #747474;
  }
  .tags {
    margin-top: 16px;
  }
  .date-arrow {
    margin-top: 24px;
    @media (min-width: breakpoints.$lg-min-width) {
      margin: 24px 8px;
    }
  }

  .right-reveal .button {
    // Stays in the tab order while visually hidden, so keyboard users can reach it.
    opacity: 0;
  }
  &.active .right-reveal .button,
  &:hover .right-reveal .button,
  &:focus-within .right-reveal .button {
    opacity: 1;
  }

  .button {
    @include flex_mixin.flex-row;
    justify-content: center;
    align-items: center;
    margin: 8px 0;
    background: rgba(0, 73, 144, 0.06);
    border-radius: 4px;
    border: none;
    min-height: 50px;
    min-width: 151px;
    cursor: pointer;
    padding: 8px;
    background: var(--event-item-hover);
    box-sizing: border-box;
    @media (min-width: breakpoints.$md) {
      @include flex_mixin.flex-column;
      min-height: $buttonMinHeight;
      min-width: $buttonMinWidth;
      margin: auto;
    }

    .add-to-calendar-trigger {
      background: none;
      border: none;
      padding: 0;
      font: inherit;
      color: inherit;
      cursor: pointer;
      align-self: stretch;
      flex: 1;
    }

    .button-container {
      @include flex_mixin.flex-row;
      justify-content: center;
      align-items: center;
      align-content: center;
      width: 100%;

      @media (min-width: breakpoints.$md) {
        @include flex_mixin.flex-column;
        justify-content: stretch;
      }

      &.open {
        @include flex_mixin.flex-column;
        justify-content: stretch;
      }

      &.add-to-calendar-trigger {
        justify-content: center;
      }

      & .button-text {
        font-style: normal;
        font-weight: 500;
        font-size: 12px;
        line-height: 16px;

        text-align: center;
        letter-spacing: 0.4px;

        color: #333333;
      }
      .back-calendar-button:hover {
        color: #ffffff;
      }
    }

    .calendar-buttons {
      @include flex_mixin.flex-column;
    }
  }
}
.event-list-item {
  @include breakpoints.screen('xs') {
    .left {
      .date {
        padding: 0 23px 0 12px;
        width: 142px;
      }
    }
  }
  @include breakpoints.screen('sm') {
    .left {
      .date {
        padding: 0 34px 0 34px;
        min-width: 142px;
      }
    }
  }
  @include breakpoints.screen('>=md') {
    .left {
      .date {
        padding: 0 0 0 34px;
        min-width: 192px;
      }
    }
  }
}
</style>
